import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthGuard } from './shared';
import { OnlyDevGuard } from './shared';
import { GraphqlModule } from './shared/services/graphql/graphql.module';
import { OverlayModule } from '@angular/cdk/overlay';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { LoggerService, initSentry } from './shared/services/logger.service';
import { CustomDateAdapter } from '../../utils/date-adapter/custom-date-adapter';
import { AuthV2Module } from './shared/services/auth-v2/auth-v2.module';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { interceptorProviders } from './shared/interceptors/interceptor-provider';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeCs from '@angular/common/locales/cs';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { provideApolloClientOptions } from '../graphql/client';
import { ConfigService } from './shared/services/config/config.service';
import { ConfigModule } from './shared/services/config/config.module';
import { applyTheme } from './shared/services/config/theme';
import * as Sentry from '@sentry/angular-ivy';

registerLocaleData(localeCs);

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    '/assets/i18n/',
    '.json?v=' + Date.now(),
  );
}

export function initializeAppFactory(configService: ConfigService) {
  return async (): Promise<void> => {
    await configService.loadConfig();
    initSentry(configService);
    await applyTheme(configService.config);
  };
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    CommonModule,
    ConfigModule,
    HttpClientModule,
    BrowserModule,
    BrowserAnimationsModule,
    GraphqlModule,
    MatNativeDateModule,
    MatSnackBarModule,
    AppRoutingModule,
    AuthV2Module,
    OverlayModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    AuthGuard,
    OnlyDevGuard,
    LoggerService,
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: LOCALE_ID, useValue: 'cs-CS' },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [ConfigService],
      multi: true,
    },
    {
      provide: APOLLO_OPTIONS,
      useFactory: provideApolloClientOptions,
      deps: [HttpLink, ConfigService],
    },
    interceptorProviders,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}